(() => {
  const theme = window.HadoriTheme || {};
  const routesRoot = (theme.routes && theme.routes.root) || '/';

  const HADORI_SUBSCRIPTION_PLANS = Object.freeze({
    2: '693011153264',
    4: '693011186032',
    6: '693011284336',
    8: '693011251568'
  });
  const HADORI_ONE_TIME_QUANTITIES = Object.freeze([1, 2, 4, 6, 8]);
  const HADORI_SUBSCRIPTION_QUANTITIES = Object.freeze([2, 4, 6, 8]);

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#039;',
    '"': '&quot;'
  }[character]));

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2
      }).format(Number(cents) / 100);
    } catch (_error) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const HADORI_SINGLE_BAR_VARIANT_ID = '54158034698608';
  const HADORI_GIFT_VARIANT_ID = '54157968212336';
  const HADORI_SOAP_SAVER_GIFT_VARIANT_ID = '54266489209200';
  const HADORI_BOGO_GIFT_VARIANTS = Object.freeze({
    '54266112475504': 4,
    '54266112508272': 6
  });
  const HADORI_BOGO_VARIANTS = Object.freeze({
    '54266112442736': '2 BARS — BUY 1, GET 1 FREE',
    '54266112475504': '4 BARS — BUY 2, GET 2 FREE',
    '54266112508272': '6 BARS — BUY 3, GET 3 FREE'
  });

  const ensureBogoTagStyles = () => {
    if (document.getElementById('HadoriBogoCartTagStyles')) return;
    const style = document.createElement('style');
    style.id = 'HadoriBogoCartTagStyles';
    style.textContent = '.hadori-bogo-tags{display:block;margin-top:8px}.hadori-bogo-tag{display:inline-flex;align-items:center;min-height:30px;max-width:100%;padding:6px 10px;color:#7f3828;background:#f0d8c9;border:1px solid #c46a4a;border-radius:5px;font-family:Arial,Helvetica,sans-serif;font-size:clamp(10px,2.6vw,13px);font-weight:800;line-height:1.15;letter-spacing:.035em;text-transform:uppercase;white-space:nowrap}';
    document.head.appendChild(style);
  };

  const isHadoriBogoItem = (item) => Boolean(
    HADORI_BOGO_VARIANTS[String(item?.variant_id || '')]
  );

  const bogoTagMarkup = (item) => {
    const label = HADORI_BOGO_VARIANTS[String(item?.variant_id || '')];
    if (!label) return '';
    return `<div class="hadori-bogo-tags"><span class="hadori-bogo-tag">${escapeHtml(label)}</span></div>`;
  };

  const isHadoriFreeGiftItem = (item) => {
    const hasGiftProperty = String(item?.properties?._hadori_free_gift) === 'true';
    const variantId = String(item?.variant_id || '');
    const isKnownGift = variantId === HADORI_GIFT_VARIANT_ID
      || variantId === HADORI_SOAP_SAVER_GIFT_VARIANT_ID;
    return hasGiftProperty || (isKnownGift && Number(item?.final_line_price || 0) === 0);
  };

  const complimentaryGiftTitle = (item) => (
    String(item?.variant_id || '') === HADORI_SOAP_SAVER_GIFT_VARIANT_ID
      ? 'Complimentary 2-Pack Soap-Saver Bags'
      : 'Complimentary Bamboo Soap Dish'
  );

  const originalLinePrice = (item) => Number(
    item?.original_line_price != null
      ? item.original_line_price
      : Number(item?.price || 0) * Number(item?.quantity || 0)
  );

  const isHadoriSingleBarItem = (item) => {
    const handle = item?.handle || item?.product_handle || '';
    const variantTitle = String(item?.variant_title || '').trim().toLowerCase();
    return handle === 'japanese-persimmon-soap' && variantTitle === '1 bar';
  };

  const isHadoriSoapItem = (item) => {
    const handle = item?.handle || item?.product_handle || '';
    return handle === 'japanese-persimmon-soap';
  };

  const barsForItem = (item) => {
    if (!isHadoriSoapItem(item)) return 0;
    const match = String(item?.variant_title || '').trim().match(/^(\d+)\s+bars?$/i);
    return match ? Number(match[1]) : 0;
  };

  const giftEligibility = (items = []) => {
    const bogoPacks = items
      .filter((item) => (
        Number(item?.quantity || 0) > 0
        && HADORI_BOGO_GIFT_VARIANTS[String(item?.variant_id)]
      ))
      .map((item) => HADORI_BOGO_GIFT_VARIANTS[String(item.variant_id)]);

    if (bogoPacks.length) {
      return `BOGO ${Math.max(...bogoPacks)} bars`;
    }

    const nativePacks = items
      .filter((item) => {
        const bars = barsForItem(item);
        return (bars === 6 || bars === 8) && Number(item.quantity || 0) > 0;
      })
      .map((item) => barsForItem(item));

    if (nativePacks.length) {
      return `${Math.max(...nativePacks)} bars`;
    }

    const legacyBars = items
      .filter(isHadoriSingleBarItem)
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    return legacyBars >= 4 ? `${legacyBars} bars` : '';
  };

  const stableProperties = (properties = {}) => Object.keys(properties || {})
    .sort()
    .map((key) => `${key}:${String(properties[key])}`)
    .join('|');

  const sellingPlanIdentity = (item) => String(
    item?.selling_plan_allocation?.selling_plan?.id
      || item?.selling_plan_allocation?.selling_plan?.name
      || 'one-time'
  );

  const hadoriDisplayGroupKey = (item) => [
    item.variant_id,
    sellingPlanIdentity(item),
    stableProperties(item.properties)
  ].join('|');

  const groupCartItemsForDisplay = (items = []) => {
    const groupedItems = [];
    const groupIndexes = new Map();

    items.forEach((item) => {
      if (!isHadoriSingleBarItem(item)) {
        groupedItems.push({ ...item });
        return;
      }

      const groupKey = hadoriDisplayGroupKey(item);
      if (!groupIndexes.has(groupKey)) {
        groupIndexes.set(groupKey, groupedItems.length);
        groupedItems.push({ ...item });
        return;
      }

      const groupedItem = groupedItems[groupIndexes.get(groupKey)];
      groupedItem.quantity += Number(item.quantity || 0);
      groupedItem.final_line_price += Number(item.final_line_price || 0);
      groupedItem.original_line_price = Number(groupedItem.original_line_price || 0)
        + Number(item.original_line_price || 0);
    });

    return groupedItems;
  };

  const drawer = () => document.querySelector('[data-cart-drawer]');

  const openCart = () => {
    const element = drawer();
    if (!element) return;
    element.hidden = false;
    document.body.classList.add('cart-open');
    requestAnimationFrame(() => element.classList.add('is-open'));
    element.querySelector('.cart-drawer__panel')?.focus();
  };

  const closeCart = () => {
    const element = drawer();
    if (!element) return;
    element.classList.remove('is-open');
    document.body.classList.remove('cart-open');
    window.setTimeout(() => {
      element.hidden = true;
    }, 220);
  };

  const renderCart = (cart) => {
    ensureBogoTagStyles();
    const itemCount = Number(cart.item_count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = itemCount;
      if (count.classList.contains('hadori-mobile-cart-count')) {
        count.hidden = itemCount < 1;
      }
    });

    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.setAttribute('aria-label', `Open bag with ${itemCount} items`);
    });

    document.querySelectorAll('.hadori-mobile-cart-link').forEach((link) => {
      link.setAttribute('aria-label', itemCount > 0 ? `View cart, ${itemCount} items` : 'View cart');
    });

    const body = document.querySelector('[data-cart-body]');
    const footer = document.querySelector('[data-cart-footer]');
    if (!body || !footer) return;

    if (!cart.items.length) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your bag is empty.</p>
          <a class="button button--terracotta button--wide" href="${routesRoot}collections/all">Shop HADORI</a>
        </div>`;
      footer.hidden = true;
      return;
    }

    const displayItems = groupCartItemsForDisplay(cart.items);
    body.innerHTML = `
      <ul class="cart-drawer__items" role="list">
        ${displayItems.map((item) => `
          <li>
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="180" height="180" loading="lazy">` : ''}
            <div>
              <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(isHadoriFreeGiftItem(item) ? complimentaryGiftTitle(item) : item.product_title)}</strong></a>
              ${isHadoriFreeGiftItem(item)
                ? '<span>Gift automatically added</span>'
                : isHadoriBogoItem(item)
                  ? bogoTagMarkup(item)
                : item.variant_title && item.variant_title !== 'Default Title' && !(item.handle === 'japanese-persimmon-soap' && item.variant_title.toLowerCase() === '1 bar')
                  ? `<span>${escapeHtml(item.variant_title)}</span>`
                  : ''}
              ${isHadoriFreeGiftItem(item)
                ? `<span class="hadori-cart-gift-price"><span class="hadori-visually-hidden">Regular price ${escapeHtml(formatMoney(originalLinePrice(item)))}. Current price free.</span><s aria-hidden="true">${escapeHtml(formatMoney(originalLinePrice(item)))}</s><strong aria-hidden="true">FREE</strong></span>`
                : isHadoriBogoItem(item)
                  ? `<span>${escapeHtml(formatMoney(item.final_line_price))}</span>`
                  : `<span>Qty ${item.quantity} · ${escapeHtml(formatMoney(item.final_line_price))}</span>`}
            </div>
          </li>`).join('')}
      </ul>`;
    footer.hidden = false;
    footer.querySelector('[data-cart-total]').textContent = formatMoney(cart.total_price);
  };

  const refreshCart = async () => {
    const response = await fetch(`${routesRoot}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to refresh your bag.');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const changeCartLine = async ({ line, key, quantity, subscribed }) => {
    const payload = key ? { id: key, quantity } : { line, quantity };
    if (subscribed) {
      const sellingPlan = HADORI_SUBSCRIPTION_PLANS[quantity];
      if (!sellingPlan) throw new Error('Choose 2, 4, 6, or 8 bars for a subscription.');
      payload.selling_plan = sellingPlan;
    }

    const response = await fetch(`${routesRoot}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Unable to update your bag.');
    return result;
  };

  const updateCartItems = async (updates) => {
    const response = await fetch(`${routesRoot}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Unable to update your bag.');
    return result;
  };

  const addCartItems = async (items) => {
    const response = await fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Unable to update your bag.');
    return result;
  };

  const cartSnapshot = async () => {
    const response = await fetch(`${routesRoot}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to refresh your bag.');
    return response.json();
  };

  const decodeGroupKeys = (value = '') => String(value)
    .split(',')
    .filter(Boolean)
    .map((key) => {
      try {
        return decodeURIComponent(key);
      } catch (_error) {
        return key;
      }
    });

  const replaceHadoriGroup = async ({ keys, quantity, subscribed }) => {
    if (keys.length) {
      const removals = {};
      keys.forEach((key) => {
        removals[key] = 0;
      });
      await updateCartItems(removals);
    }

    if (quantity <= 0) return;

    const line = {
      id: Number(HADORI_SINGLE_BAR_VARIANT_ID),
      quantity
    };
    if (subscribed) {
      const sellingPlan = HADORI_SUBSCRIPTION_PLANS[quantity];
      if (!sellingPlan) throw new Error('Choose 2, 4, 6, or 8 bars for a subscription.');
      line.selling_plan = Number(sellingPlan);
    }
    await addCartItems([line]);
  };

  const soapSaverEligibility = (items = []) => {
    const hasSixBarBogo = items.some((item) => (
      String(item?.variant_id || '') === '54266112508272'
      && Number(item?.quantity || 0) > 0
    ));
    return hasSixBarBogo ? 'BOGO 6 bars' : '';
  };

  const syncComplimentaryGift = async ({ cart, variantId, expectedGiftFor }) => {
    const giftItems = cart.items.filter((item) => String(item.variant_id) === variantId);

    if (expectedGiftFor) {
      const giftIsCurrent = giftItems.length === 1
        && Number(giftItems[0].quantity) === 1
        && giftItems[0].properties?._hadori_free_gift === 'true'
        && giftItems[0].properties?._hadori_gift_for === expectedGiftFor;

      if (giftIsCurrent) return false;

      if (giftItems.length) {
        const removals = {};
        giftItems.forEach((item) => {
          removals[item.key] = 0;
        });
        await updateCartItems(removals);
      }

      await addCartItems([{
        id: Number(variantId),
        quantity: 1,
        properties: {
          _hadori_free_gift: 'true',
          _hadori_gift_for: expectedGiftFor
        }
      }]);
      return true;
    }

    if (!giftItems.length) return false;
    const removals = {};
    giftItems.forEach((item) => {
      removals[item.key] = 0;
    });
    await updateCartItems(removals);
    return true;
  };

  const syncHadoriGift = async () => {
    let cart = await cartSnapshot();
    const expectedDishFor = giftEligibility(cart.items);
    const expectedSoapSaverFor = soapSaverEligibility(cart.items);
    const dishChanged = await syncComplimentaryGift({
      cart,
      variantId: HADORI_GIFT_VARIANT_ID,
      expectedGiftFor: expectedDishFor
    });

    if (dishChanged) cart = await cartSnapshot();

    await syncComplimentaryGift({
      cart,
      variantId: HADORI_SOAP_SAVER_GIFT_VARIANT_ID,
      expectedGiftFor: expectedSoapSaverFor
    });
  };

  const upgradeCartToFour = async (button) => {
    const form = button.closest('[data-hadori-cart-form]');
    const card = button.closest('[data-cart-upgrade-card]');
    if (!form || !card || form.dataset.upgradeBusy === 'true') return;
    const error = card.querySelector('[data-cart-upgrade-error]');
    const originalLabel = button.textContent;
    const controls = [...form.querySelectorAll('button, input')].map((element) => ({ element, disabled: element.disabled }));
    const express = form.querySelector('.additional-checkout-buttons');
    const expressWasInert = express?.inert;
    form.dataset.upgradeBusy = 'true';
    card.setAttribute('aria-busy', 'true');
    controls.forEach(({ element }) => { element.disabled = true; });
    if (express) express.inert = true;
    error.hidden = true;
    button.textContent = button.dataset.updating;

    try {
      const cart = await cartSnapshot();
      const items = cart.items.filter(isHadoriSoapItem);
      const quantity = items.reduce((sum, item) => sum + Number(item.quantity), 0);
      const subscribed = button.dataset.subscription === 'true';
      const expectedKeys = decodeGroupKeys(button.dataset.cartKeys).sort();
      const currentKeys = items.map((item) => item.key).sort();
      const compatible = items.length > 0
        && items.every(isHadoriSingleBarItem)
        && quantity === Number(button.dataset.quantity)
        && [1, 2].includes(quantity)
        && JSON.stringify(expectedKeys) === JSON.stringify(currentKeys)
        && !cart.items.some(isHadoriBogoItem)
        && items.every((item) => hadoriDisplayGroupKey(item) === hadoriDisplayGroupKey(items[0]))
        && items.every((item) => subscribed
          ? sellingPlanIdentity(item) === HADORI_SUBSCRIPTION_PLANS[2]
          : !item.selling_plan_allocation);
      if (!compatible || (subscribed && items.length !== 1)) {
        throw new Error(button.dataset.cartChanged);
      }

      if (subscribed) {
        await changeCartLine({
          line: cart.items.findIndex((item) => item.key === items[0].key) + 1,
          quantity: 4,
          subscribed: true
        });
      } else if (items.length === 1) {
        await changeCartLine({ key: items[0].key, quantity: 4, subscribed: false });
      } else {
        const updates = Object.fromEntries(items.map((item, index) => [item.key, index === 0 ? 4 : 0]));
        await updateCartItems(updates);
      }

      await syncHadoriGift();
      window.location.reload();
    } catch (cartError) {
      error.textContent = cartError.message === button.dataset.cartChanged
        ? button.dataset.cartChanged
        : button.dataset.error;
      error.hidden = false;
      form.dataset.upgradeBusy = 'false';
      card.setAttribute('aria-busy', 'false');
      controls.forEach(({ element, disabled }) => { element.disabled = disabled; });
      if (express) express.inert = expressWasInert;
      button.textContent = originalLabel;
      button.focus();
    }
  };

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-hadori-cart-form][data-upgrade-busy="true"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  const updateCartForm = async (form) => {
    const button = form.querySelector('button[name="update"]');
    const error = form.querySelector('[data-cart-error]');
    const inputs = [...form.querySelectorAll('[data-cart-line-quantity]')]
      .sort((a, b) => Number(b.dataset.cartLine) - Number(a.dataset.cartLine));

    if (button) button.disabled = true;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      let cartChanged = false;
      for (const input of inputs) {
        const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
        const originalQuantity = Math.max(0, Number.parseInt(input.dataset.originalQuantity, 10) || 0);
        if (quantity === originalQuantity) continue;

        const subscribed = input.dataset.subscription === 'true';
        const isHadoriBundle = input.dataset.hadoriBundleQuantity === 'true';
        if (isHadoriBundle) {
          const allowed = subscribed ? HADORI_SUBSCRIPTION_QUANTITIES : HADORI_ONE_TIME_QUANTITIES;
          if (!allowed.includes(quantity)) {
            throw new Error(subscribed
              ? 'Subscription quantities must be 2, 4, 6, or 8 bars.'
              : 'Choose 1, 2, 4, 6, or 8 bars.');
          }
        }

        if (input.dataset.cartGroupKeys) {
          await replaceHadoriGroup({
            keys: decodeGroupKeys(input.dataset.cartGroupKeys),
            quantity,
            subscribed
          });
          cartChanged = true;
        } else {
          await changeCartLine({
            line: Number(input.dataset.cartLine),
            key: input.dataset.cartKey,
            quantity,
            subscribed: isHadoriBundle && subscribed
          });
          cartChanged = true;
        }
      }
      if (cartChanged) await syncHadoriGift();
      window.location.reload();
    } catch (cartError) {
      if (button) button.disabled = false;
      if (error) {
        error.textContent = cartError.message;
        error.hidden = false;
      }
    }
  };

  const removeHadoriGroup = async (button) => {
    const form = button.closest('[data-hadori-cart-form]');
    const error = form?.querySelector('[data-cart-error]');
    button.disabled = true;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      await replaceHadoriGroup({
        keys: decodeGroupKeys(button.dataset.cartGroupKeys),
        quantity: 0,
        subscribed: false
      });
      await syncHadoriGift();
      window.location.reload();
    } catch (cartError) {
      button.disabled = false;
      if (error) {
        error.textContent = cartError.message;
        error.hidden = false;
      }
    }
  };

  const showGalleryMedia = (root, mediaId, index) => {
    if (!mediaId) return;
    root.querySelectorAll('[data-media-id]').forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      media.hidden = !active;
      media.classList.toggle('is-active', active);
    });
    root.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
      const active = String(thumb.dataset.mediaThumb) === String(mediaId);
      thumb.classList.toggle('pdp-thumb--active', active);
      thumb.setAttribute('aria-pressed', String(active));
    });
    const gallery = root.querySelector('[data-product-gallery]');
    if (gallery && index) {
      gallery.dataset.galleryIndex = String(index);
    }

  };

  const navigateGallery = (gallery, direction) => {
    if (!gallery) return;
    const root = gallery.closest('[data-product-root]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-media-thumb]'));
    if (!root || !thumbs.length) return;

    const activeThumb = gallery.querySelector('.pdp-thumb--active');
    const currentIndex = Math.max(0, thumbs.indexOf(activeThumb));
    const nextIndex = (currentIndex + direction + thumbs.length) % thumbs.length;
    const nextThumb = thumbs[nextIndex];
    showGalleryMedia(root, nextThumb.dataset.mediaThumb, nextThumb.dataset.mediaIndex);
    nextThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  let gallerySwipeStart = null;

  const updateVariant = (input) => {
    const root = input.closest('[data-product-root]');
    if (!root) return;

    root.querySelectorAll('[data-variant-card]').forEach((card) => {
      card.classList.toggle('pdp-bundle--selected', card.contains(input));
    });

    const hiddenId = root.querySelector('[data-final-offer-form] input[name="id"]')
      || root.querySelector('[data-variant-id]');
    const quantity = root.querySelector('[data-final-offer-form] input[name="quantity"]');
    const submit = root.querySelector('[data-product-submit]');
    const available = input.dataset.variantAvailable === 'true';
    const bars = Math.max(1, Number(input.dataset.variantBars || 1));
    const singleBar = root.querySelector('[data-variant-option][data-variant-bars="1"]');
    const oneBarVariantId = singleBar?.dataset.variantId || singleBar?.value || '54158034698608';

    // Keep the display variant for cards, gallery, and URL state, but submit the
    // single-bar variant at the customer's actual selected quantity.
    if (hiddenId) {
      hiddenId.value = oneBarVariantId;
      hiddenId.setAttribute('value', oneBarVariantId);
    }
    if (quantity) {
      quantity.value = String(bars);
      quantity.setAttribute('value', String(bars));
    }
    root.querySelector('[data-product-price]').textContent = input.dataset.variantPrice;
    root.querySelector('[data-product-bars]').textContent = input.dataset.variantTitle;
    root.querySelector('[data-product-per-bar]').textContent = input.dataset.variantPerBar;
    const finalBars = root.querySelector('[data-final-bars]');
    const finalPrice = root.querySelector('[data-final-price]');
    if (finalBars) finalBars.textContent = input.dataset.variantTitle;
    if (finalPrice) finalPrice.textContent = input.dataset.variantPrice;
    const mobileSelection = root.querySelector('[data-mobile-selection]');
    const mobileShipping = root.querySelector('[data-mobile-shipping]');
    if (mobileSelection) mobileSelection.textContent = `${input.dataset.variantTitle} · ${input.dataset.variantPrice}`;
    if (mobileShipping) mobileShipping.textContent = bars >= 4 ? 'Free U.S. shipping' : 'Made in Japan';

    if (submit) {
      submit.disabled = !available;
      submit.classList.remove('pdp-add--added');
      submit.querySelector('[data-product-submit-text]').textContent = available
        ? `Add ${input.dataset.variantTitle} to bag — ${input.dataset.variantPrice}`
        : 'Sold out';
    }

    root.querySelectorAll('[data-product-submit-proxy]').forEach((button) => {
      button.disabled = !available;
      button.textContent = available ? (button.closest('.pdp-mobile-buy') ? 'ADD TO CART' : 'Add selected set to bag') : 'Sold out';
    });

    if (input.dataset.variantMedia) {
      const thumb = root.querySelector(`[data-media-thumb="${CSS.escape(input.dataset.variantMedia)}"]`);
      showGalleryMedia(root, input.dataset.variantMedia, thumb?.dataset.mediaIndex);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', input.value);
    window.history.replaceState({}, '', url);
  };

  const addProduct = async (form) => {
    const root = form.closest('[data-product-root]');
    const button = form.querySelector('[data-product-submit]');
    const text = button?.querySelector('[data-product-submit-text]');
    const error = form.querySelector('[data-product-error]');
    const previousText = text?.textContent || 'Add to bag';

    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (button) button.disabled = true;
    if (text) text.textContent = 'Adding to bag…';

    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This set could not be added.');

      await refreshCart();
      if (button) button.classList.add('pdp-add--added');
      if (text) text.textContent = 'Added to bag ✓';
      root?.querySelectorAll('[data-product-submit-proxy]').forEach((proxy) => {
        proxy.textContent = 'Added ✓';
      });
      openCart();

      window.setTimeout(() => {
        if (button) button.disabled = false;
        if (text) text.textContent = previousText;
        root?.querySelectorAll('[data-product-submit-proxy]').forEach((proxy) => {
          proxy.textContent = proxy.closest('.pdp-mobile-buy') ? 'ADD TO CART' : 'Add selected set to bag';
        });
      }, 1800);
    } catch (addError) {
      if (button) button.disabled = false;
      if (text) text.textContent = previousText;
      if (error) {
        error.textContent = addError.message;
        error.hidden = false;
      }
    }
  };

  document.addEventListener('touchstart', (event) => {
    if (!window.matchMedia('(max-width: 780px)').matches || event.touches.length !== 1) return;
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const stage = target?.closest('.pdp-gallery__stage');
    if (!stage) return;

    const touch = event.touches[0];
    gallerySwipeStart = {
      stage,
      x: touch.clientX,
      y: touch.clientY
    };
  }, { passive: true });

  document.addEventListener('touchend', (event) => {
    if (!gallerySwipeStart || event.changedTouches.length !== 1) {
      gallerySwipeStart = null;
      return;
    }

    const start = gallerySwipeStart;
    gallerySwipeStart = null;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
    navigateGallery(start.stage.closest('[data-product-gallery]'), deltaX < 0 ? 1 : -1);
  }, { passive: true });

  document.addEventListener('touchcancel', () => {
    gallerySwipeStart = null;
  }, { passive: true });

  document.addEventListener('change', (event) => {
    const variant = event.target.closest('[data-variant-option]');
    if (variant) updateVariant(variant);
  });

  document.addEventListener('submit', (event) => {
    const cartForm = event.target.closest('[data-hadori-cart-form]');
    if (cartForm && event.submitter?.name === 'update') {
      event.preventDefault();
      updateCartForm(cartForm);
      return;
    }

    // Let Shopify's native product form submit normally. Product forms
    // redirect to the cart by default and retain Shopify's standard ATC path.
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
  });

  document.addEventListener('click', (event) => {
    const groupRemove = event.target.closest('[data-hadori-group-remove]');
    if (groupRemove) {
      event.preventDefault();
      removeHadoriGroup(groupRemove);
      return;
    }

    const mediaThumb = event.target.closest('[data-media-thumb]');
    if (mediaThumb) {
      const root = mediaThumb.closest('[data-product-root]');
      showGalleryMedia(root, mediaThumb.dataset.mediaThumb, mediaThumb.dataset.mediaIndex);
      return;
    }

    const galleryNav = event.target.closest('[data-gallery-prev], [data-gallery-next]');
    if (galleryNav) {
      const direction = galleryNav.matches('[data-gallery-next]') ? 1 : -1;
      navigateGallery(galleryNav.closest('[data-product-gallery]'), direction);
      return;
    }

    const upgradeButton = event.target.closest('[data-cart-upgrade]');
    if (upgradeButton) {
      event.preventDefault();
      upgradeCartToFour(upgradeButton);
      return;
    }

    if (event.target.closest('[data-cart-open]')) {
      openCart();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeCart();
      return;
    }

    const mobileLink = event.target.closest('.hadori-mobile-menu a');
    if (mobileLink) mobileLink.closest('details')?.removeAttribute('open');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer() && !drawer().hidden) closeCart();
  });

  const setupMobileBuyVisibility = () => {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const mobileBuy = root.querySelector('[data-mobile-buy]');
      const trigger = root.querySelector('[data-mobile-buy-trigger]');
      if (!mobileBuy || !trigger || mobileBuy.dataset.visibilityReady === 'true') return;

      mobileBuy.dataset.visibilityReady = 'true';
      const mobileQuery = window.matchMedia('(max-width: 780px)');
      let scheduled = false;

      const update = () => {
        scheduled = false;
        const reachedTrigger = mobileQuery.matches
          && trigger.getBoundingClientRect().top <= window.innerHeight;

        mobileBuy.classList.toggle('pdp-mobile-buy--visible', reachedTrigger);
        mobileBuy.setAttribute('aria-hidden', String(!reachedTrigger));
      };

      const scheduleUpdate = () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', scheduleUpdate, { passive: true });
      mobileQuery.addEventListener?.('change', scheduleUpdate);
      update();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupMobileBuyVisibility, { once: true });
  } else {
    setupMobileBuyVisibility();
  }
})();
